import { CommonTooltips } from "@mcleod/common";
import { ClickEvent, DataDisplayEvent, DataSourceExecutionEvent, Label, Layout, Textbox } from "@mcleod/components";
import { Alignment, Api, Currency, CurrencyUtil, FieldUpdateEvent, ModelRow, StringUtil } from "@mcleod/core";
import { ModelOrders, OrderTabView } from "@mcleod/dispatch";

import { TransmissionStatusQuickInfo } from "./TransmissionStatusQuickInfo";
import { AutogenLayoutEdiOrderOverview } from "./autogen/AutogenLayoutEdiOrderOverview";
import { EdiProfileOrderQuickInfo } from "./EdiProfileOrderQuickInfo";
import { setCustomerQuickInfo } from "./EdiUtility";

export class EdiOrderOverview extends AutogenLayoutEdiOrderOverview {
    override onLoad() {
        this.setupLabelTransmitStatusTooltipContent();
        this.setupLabelPartnerNameTooltipContent();
    }

    private setupLabelTransmitStatusTooltipContent() {
        this.labelTransmitStatus.tooltipDynamicContent = async () => {
            const activeRow = this.mainDataSource?.activeRow;
            if (activeRow.get("direction") === "I" && activeRow.get("reply_batch_id") > "0") {
                const layout = await Layout.getLoadedLayout("lme/datafusion/TransmissionStatusQuickInfo") as TransmissionStatusQuickInfo;
                await layout.sourceEdiLog.search({
                    direction: "O",
                    func_batch: this.mainDataSource.activeRow.get("reply_batch_id"),
                    batch: this.mainDataSource.activeRow.get("reply_batch_id"),
                    func_receiver_id: this.mainDataSource.activeRow.get("reply_partner_id"),
                    receiver_id: this.mainDataSource.activeRow.get("reply_altpartnerid"),
                    func_version: this.mainDataSource.activeRow.get("reply_version"),
                    transaction_type: 'R',
                    search_receiver_id: this.mainDataSource.activeRow.get("reply_partner_id")
                });
                return layout;
            }
            return null;
        };
    }

    labelTenderIdOnDataDisplay(event: DataDisplayEvent) {
        const label = <Label>event.target;
        label.color = "default.dark";
    }

    sourceOrdersAfterExecution(event) {
        const row = this.mainDataSource.activeRow;

        this.textboxCommodity.text = row.getFirstLookupModelData("commodity_id")?.get("descr");
        if (event?.getAction() == "search") {
            this.setCarrierRateData();
            row.addAfterFieldUpdateListener((event: FieldUpdateEvent) => this.orderFieldUpdated(event))
            this.panelCommodity.visible = "D" != this.mainDataSource.activeRow.get("status");

            this._showFieldsByDirection(row);
        }
    }

    private _addCarrierToolTip() {
        const carrierId = this.mainDataSource.activeRow?.get("carrier_id");
        const carrierAssignmentRow = this.sourceCarriersForAssignment.activeRow;
        const name = this.sourceCarrierPayee.activeRow?.get("name");
        let tooltipData = null;

        tooltipData = new ModelRow("lme/powerbroker/carriers-for-assignment", false, {
            id: carrierId,
            name: name,
            phone_number: carrierAssignmentRow?.get("phone_number"),
            icc_number: carrierAssignmentRow?.get("icc_number"),
            dot_number: carrierAssignmentRow?.get("dot_number")
        })
        CommonTooltips.setTooltipContentLayout(this.textboxCarrierName, tooltipData, "lme/powerbroker/CarrierQuickInfo", { minWidth: 100, minHeight: 100 });
        this.textboxCarrierName.tooltipDynamicOptions = { position: Alignment.RIGHT };
    }
    private _showFieldsByDirection(row: ModelRow<any>) {
        const customerId = row?.get("customer_id");
        const tenderDirection = row?.get("direction");
        const carrierId = row?.get("carrier_id");
        const requiresReply = row?.get("requires_reply_outbound");
        const replyReceived = row?.get("reply_received");
        const replyTransmitted = row?.get("transmitted");
        if ("I" == tenderDirection) {
            this.textboxCarrierName.visible = false;
            if (customerId != null)
                this.sourceCustomer.search({ "id": customerId });

            this.labelTransmitted.text = "Transmitted";
            this.labelTransmitStatus.text = replyTransmitted;

            this.panelCarrierPay.visible = false;
        } else if ("O" == tenderDirection) {
            this.textboxName.visible = false;
            if (carrierId != null) {
                this.sourceCarrierPayee.search({ "id": carrierId });
            }
            this.labelReply.text = "Carrier Reply";
            this.labelTransmitted.text = "Status";

            if ("N" == requiresReply) {
                this.labelTransmitStatus.text = "Not Required";
            } else if ("Y" == replyReceived) {
                this.labelTransmitStatus.text = "Received";
            } else {
                this.labelTransmitStatus.text = "Not Received";
            }
            this.panelInboundCharges.visible = false;
        }

    }

    orderFieldUpdated(event: FieldUpdateEvent) {
        if ("customer_id" == event.fieldName && StringUtil.isEmptyString(event.newValue)) {
            this.customerChanged(null);
        }
        else if ("commodity_id" == event.fieldName) {
            const row = this.mainDataSource.activeRow;
            this.textboxCommodity.text = row.getFirstLookupModelData("commodity_id")?.get("descr") || "--";
        }
    }

    customerChanged(customerData: ModelRow<any>) {
        this.sourceCustomer.data = [];
        if (customerData != null) {
            this.sourceCustomer.data = [customerData];
            this.sourceCustomer.setRowIndexWithoutUpdate(0);
        }
        this.sourceCustomer.displayDataInBoundComponents();
    }

    setupLabelPartnerNameTooltipContent() {
        this.labelPartnerName.tooltipDynamicContent = async () => {
            const row = this.sourceViewEdiOrder.activeRow;
            const partnerID = row?.get("partner_id");
            const version = row?.get("version");
            const direction = row?.get("direction");
            if (partnerID != null && version != null && direction != null) {
                const batch = row?.get("gs06_group_cntlno");
                const createDate = row?.get("create_date");
                const layout = await Layout.getLoadedLayout("lme/datafusion/EdiProfileOrderQuickInfo") as EdiProfileOrderQuickInfo;
                await layout.sourceEdiorderProfile?.search({
                    partner_id: partnerID,
                    version,
                    direction,
                    batch,
                    trans_type: "T",
                    create_date: createDate
                });
                return layout;
            }
            return null;
        };
    }

    displayCustomer(event: DataDisplayEvent) {
         const customerNameLabel = <Label>event.target;
                 const row = this.sourceViewEdiOrder.activeRow;
                 setCustomerQuickInfo(customerNameLabel, row);
    }

    setCarrierRateData() {
        const moveId = this.mainDataSource.activeRow.get("movement.id");
        if (moveId != null) {
            Api.search("lme/dispatch/calculate-override-pay",
                {
                    "movement_id": moveId
                }).then(response => {
                    const setCurrency = function (textbox: Textbox, currency: Currency) {
                        if (currency != null) {
                            textbox.text = CurrencyUtil.formatCurrency(currency);
                        }
                    }
                });
        }

    }

    sourceOrdersOnDisplay(event) {
        this.labelTrackingRequired.visible = this.mainDataSource.activeRow?.get("show_tracking_badge", false);
    }

    async buttonMoveDetailsOnClick(event: ClickEvent) {

        const orderId = this.sourceViewEdiOrder.activeRow?.get("order_id");
        const orderRow = await new ModelOrders().searchSingle({ id: orderId }, "lme/dispatch/Orders");
        const moveId = orderRow.get("curr_movement_id");
        if (moveId != null) {
            OrderTabView.navigateTo({ orderId: orderId, key: moveId, newTab: true})
        }
    }

    /** This is an event handler for the afterExecution event of sourceCarrierPayee.  */
    sourceCarrierPayeeAfterExecution(event: DataSourceExecutionEvent) {
        this.sourceCarriersForAssignment.search({ "id": this.mainDataSource.activeRow?.get("carrier_id") });
    }

    /** This is an event handler for the afterExecution event of sourceCarriersForAssignment.  */
    sourceCarriersForAssignmentAfterExecution(event: DataSourceExecutionEvent) {
        this._addCarrierToolTip();
    }
}
